import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { ResourceHubItem } from '@/pages/resource-hub/shared/types';
import { Tag } from '@/components/Tag';
import {
  useResourceHubListStore,
  type ResourceCategoryNode,
} from '@/pages/resource-hub/list/store/useResourceHubListStore';
import useBookmark from '@/components/Bookmark/useBookmark';
import { Enum_Bookmark_Category } from '@edwin-group/people-graphql';
import classNames from 'classnames';

export interface ResourceCardProps {
  resource: ResourceHubItem;
  linkText?: string;
  onRemoveFromBookmark?: () => void;
  isResourceHub?: boolean;
  removeFromBookmarksText?: string;
  hideStatusBadges?: boolean;
  categoryColor?: string | null;
  className?: string;
}

const isCategoryInTree = (node: ResourceCategoryNode, categoryId: string): boolean => {
  if (node.documentId === categoryId) return true;
  if (!node.subCategories?.length) return false;
  return node.subCategories.some((sub) => isCategoryInTree(sub, categoryId));
};

export const ResourceCard = ({
  resource,
  linkText = 'Go to resource',
  onRemoveFromBookmark,
  removeFromBookmarksText = 'Remove from bookmarks',
  hideStatusBadges = false,
  categoryColor,
  className,
}: ResourceCardProps) => {
  const rootCategories = useResourceHubListStore((state) => state.rootCategories);
  const { handleClick: handleBookmarkClick } = useBookmark({
    relationId: resource.documentId || '',
  });

  const isNew = useMemo(() => {
    if (resource.resourceStatus !== 'New') return false;
    if (!resource.expireResourceStatusAt) return true;
    return new Date(resource.expireResourceStatusAt) > new Date();
  }, [resource.resourceStatus, resource.expireResourceStatusAt]);

  const isUpdated = useMemo(() => {
    if (resource.resourceStatus !== 'Updated') return false;
    if (!resource.expireResourceStatusAt) return true;
    return new Date(resource.expireResourceStatusAt) > new Date();
  }, [resource.resourceStatus, resource.expireResourceStatusAt]);

  const categoryBorderColor = useMemo(() => {
    if (categoryColor) return categoryColor;

    const resourceCategoryColor = (resource.category as { color?: string | null } | null | undefined)?.color;
    if (resourceCategoryColor) return resourceCategoryColor;

    const categoryId = resource.category?.documentId;
    if (!categoryId || rootCategories.length === 0) return undefined;

    for (const root of rootCategories) {
      if (root.documentId === categoryId || isCategoryInTree(root, categoryId)) {
        return root.color ?? undefined;
      }
    }

    return undefined;
  }, [categoryColor, resource.category, rootCategories]);

  const statusBadge = useMemo(() => {
    if (isNew) {
      return (
        <span className="rounded-[2px] bg-card-bg-new/25 px-[3px] py-px font-haffersq text-sh-12-18 font-normal text-black">
          New
        </span>
      );
    }
    if (isUpdated) {
      return (
        <span className="rounded-[4px] bg-teal-accent/20 px-2 py-1 font-haffersq text-sh-12-18 font-normal text-black">
          Updated
        </span>
      );
    }
    return null;
  }, [isNew, isUpdated]);

  const filesCount = resource.assetsCount ?? 0;
  const filesLabel = useMemo(() => {
    if (filesCount === 0) return null;
    return filesCount === 1 ? '1 file' : `${filesCount} files`;
  }, [filesCount]);

  return (
    <div
      className={classNames(
        'relative flex h-full origin-center flex-col rounded-[6px] border border-teal-accent/50 bg-light-green p-[16px] transition-transform duration-300 ease-out will-change-transform hover:z-10 hover:scale-[1.03]',
        className,
      )}
      style={categoryBorderColor ? { borderColor: categoryBorderColor } : undefined}>
      <div className="mb-[8px] flex items-start justify-between gap-3">
        <h3 className="mb-0 font-haffer text-sh-14-20 font-bold text-black">{resource.title}</h3>
        {!hideStatusBadges && <div className="flex items-center gap-2">{statusBadge}</div>}
      </div>

      <div className="mb-sh-15">
        <Tag label={resource.type?.type ?? 'Resource Type'} textColor="text-black-200" bgColor={'bg-white'} />
      </div>

      {filesLabel && (
        <div className="mb-[8px] flex items-center gap-1.5 text-gray-500">
          <svg xmlns="http://www.w3.org/2000/svg" width="11" height="14" viewBox="0 0 11 14" fill="none">
            <path
              d="M6.75 0.668457V3.25005C6.75 3.60008 6.75 3.77509 6.81812 3.90879C6.87804 4.02639 6.97365 4.122 7.09126 4.18192C7.22495 4.25004 7.39997 4.25004 7.75 4.25004H10.3316M6.75 9.875H3M8 7.375H3M10.5 5.49264V10C10.5 11.0501 10.5 11.5751 10.2956 11.9762C10.1159 12.329 9.82904 12.6159 9.47623 12.7956C9.07515 13 8.5501 13 7.5 13H3.5C2.4499 13 1.92485 13 1.52377 12.7956C1.17096 12.6159 0.884125 12.329 0.704363 11.9762C0.5 11.5751 0.5 11.0501 0.5 10V3.5C0.5 2.4499 0.5 1.92485 0.704363 1.52377C0.884125 1.17096 1.17096 0.884125 1.52377 0.704363C1.92485 0.5 2.4499 0.5 3.5 0.5H5.50736C5.96597 0.5 6.19527 0.5 6.41106 0.551806C6.60238 0.597738 6.78527 0.673496 6.95303 0.7763C7.14225 0.892253 7.3044 1.0544 7.62868 1.37868L9.62132 3.37132C9.9456 3.6956 10.1077 3.85775 10.2237 4.04697C10.3265 4.21473 10.4023 4.39762 10.4482 4.58894C10.5 4.80473 10.5 5.03403 10.5 5.49264Z"
              stroke="#E7897F"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="font-haffersq text-sh-12-18 font-normal text-people-gray-400">{filesLabel}</span>
        </div>
      )}

      {resource.briefDescription && (
        <p className="line-clamp-2 font-haffersq text-sh-14-20 font-normal text-dark-grey">
          {resource.briefDescription}
        </p>
      )}

      <div className="mt-auto flex flex-col items-start gap-y-2 pt-[8px]">
        <Link
          to={`/resource/${resource.slug}`}
          className="font-haffer text-sh-12-18 font-bold !text-people-teal-600 hover:underline"
          aria-label={`Resource: ${resource.title}`}>
          {linkText}
        </Link>
        {onRemoveFromBookmark && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              handleBookmarkClick(
                resource,
                resource.documentId || '',
                'RESOURCE' as Enum_Bookmark_Category,
                onRemoveFromBookmark,
              );
            }}
            className="cursor-pointer border-0 bg-transparent p-0 font-haffer text-sh-12-18 font-bold !text-people-teal-600 hover:underline">
            {removeFromBookmarksText}
          </button>
        )}
      </div>
    </div>
  );
};

export default ResourceCard;
